var _a;
import { DateTime } from "luxon";
import * as rruleLib from "rrule";
const RRule = rruleLib.RRule || ((_a = rruleLib.default) === null || _a === void 0 ? void 0 : _a.RRule);
import { convertRRule } from "./rrule.js";
export const calculateNextOccurrence = (event, referenceDate = DateTime.now(), opts = {}) => {
    var _a, _b;
    // Add debug function that only logs when verbose is true
    const debug = (message, ...args) => {
        if (opts.verbose) {
            console.log(`[NextOccurrence] ${message}`, ...args);
        }
    };
    try {
        const { rrule, recurring, frequency, recurringEnd, date } = event;
        const startDate = DateTime.fromJSDate(date.start);
        const zone = ((_a = event.timezone) === null || _a === void 0 ? void 0 : _a[0]) || "utc";
        debug("Using timezone:", zone);
        debug("Input parameters:", {
            eventName: event.name,
            recurring,
            frequency,
            recurringEnd,
            date,
            rrule,
            startDate: startDate.toISO(),
            timezone: zone,
            referenceDate: referenceDate instanceof DateTime ? referenceDate.toISO() : referenceDate,
        });
        // Normalize reference date first
        if (referenceDate instanceof Date && !isNaN(referenceDate.getTime())) {
            // Convert JS Date to Luxon DateTime
            referenceDate = DateTime.fromJSDate(referenceDate);
            debug("Normalized reference date from JS Date:", referenceDate.toISO());
        }
        else if (!(referenceDate instanceof DateTime)) {
            // Default to current date if it's not a valid Date or DateTime
            referenceDate = DateTime.now();
            debug("Using current date as reference:", referenceDate.toISO());
        }
        // For non-recurring events, check if ongoing or future
        if (!rrule && !recurring) {
            debug("Event is not recurring, returning original date");
            return date;
        }
        // For recurring events, check if the CURRENT occurrence is ongoing or in future
        // Use strict > for end date comparison so events that just ended don't count as ongoing
        const endDate = DateTime.fromJSDate(date.end);
        const isOngoing = startDate <= referenceDate && endDate > referenceDate;
        const isFuture = startDate > referenceDate;
        debug("Event timing status:", { isOngoing, isFuture });
        // Only return original date if it's truly ongoing (not just ended) or in the future
        if (isOngoing || isFuture) {
            debug("Event is ongoing or in future, returning original date");
            return date;
        }
        let rule;
        let needsRRuleCorrection = false;
        let correctedDtstart;
        if (rrule === null || rrule === void 0 ? void 0 : rrule.origOptions) {
            const convertedRule = convertRRule(rrule, zone, {
                verbose: opts.verbose,
                referenceStartDate: date.start
            });
            debug("Using existing RRule with converted options:", convertedRule);
            rule = new RRule(convertedRule);
            // Check if original dtstart differs from date.start (needs DB correction)
            if (((_b = rrule.origOptions) === null || _b === void 0 ? void 0 : _b.dtstart) && date.start) {
                let originalDtstart = null;
                let isMalformed = false;
                if (rrule.origOptions.dtstart instanceof Date) {
                    originalDtstart = DateTime.fromJSDate(rrule.origOptions.dtstart).toUTC();
                }
                else if (typeof rrule.origOptions.dtstart === 'string') {
                    originalDtstart = DateTime.fromISO(rrule.origOptions.dtstart, { zone: 'utc' }).toUTC();
                    // Check if the string is malformed (missing seconds/timezone like "2025-01-28T13:00")
                    const isoRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;
                    if (!isoRegex.test(rrule.origOptions.dtstart)) {
                        isMalformed = true;
                        debug("RRule dtstart is malformed:", rrule.origOptions.dtstart);
                    }
                }
                // Flag for correction if: 1) dtstart is malformed, OR 2) dtstart is valid but time differs
                if (isMalformed || !(originalDtstart === null || originalDtstart === void 0 ? void 0 : originalDtstart.isValid)) {
                    // Malformed or invalid dtstart - definitely needs correction
                    needsRRuleCorrection = true;
                    correctedDtstart = date.start;
                    debug("RRule dtstart is invalid/malformed, flagging for correction");
                }
                else if (originalDtstart && originalDtstart.isValid) {
                    // Valid dtstart - check if time differs
                    const refDate = DateTime.fromJSDate(date.start).toUTC();
                    const refHour = refDate.hour;
                    const refMinute = refDate.minute;
                    const origHour = originalDtstart.hour;
                    const origMinute = originalDtstart.minute;
                    if (refHour !== origHour || Math.abs(refMinute - origMinute) > 1) {
                        needsRRuleCorrection = true;
                        correctedDtstart = date.start;
                        debug("RRule dtstart time mismatch detected:", {
                            originalTime: `${origHour}:${origMinute}`,
                            referenceTime: `${refHour}:${refMinute}`,
                            correctedDtstart
                        });
                    }
                }
            }
        }
        else {
            debug("Creating new RRule with frequency:", frequency);
            switch (frequency) {
                case "daily":
                    rule = new RRule({
                        freq: RRule.DAILY,
                        dtstart: startDate.toJSDate(),
                        until: recurringEnd ? new Date(recurringEnd) : undefined,
                    });
                    break;
                case "weekly":
                    rule = new RRule({
                        freq: RRule.WEEKLY,
                        dtstart: startDate.toJSDate(),
                        until: recurringEnd ? new Date(recurringEnd) : undefined,
                    });
                    break;
                case "monthly":
                    rule = new RRule({
                        freq: RRule.MONTHLY,
                        dtstart: startDate.toJSDate(),
                        until: recurringEnd ? new Date(recurringEnd) : undefined,
                    });
                    break;
                default:
                    debug("Invalid frequency, returning original date");
                    return date;
            }
        }
        const after = (referenceDate === null || referenceDate === void 0 ? void 0 : referenceDate.toJSDate)
            ? referenceDate.toJSDate()
            : referenceDate;
        debug("Calculating next occurrence after:", after);
        const nextOccurence = rule.after(after);
        if (!nextOccurence) {
            debug("No next occurrence found, returning original date");
            return date;
        }
        // Timezone-agnostic approach: Convert calculated UTC result back to original timezone context
        let nextStartDate, nextEndDate;
        // Calculate duration once from original event times
        const duration = DateTime.fromJSDate(date.end).diff(DateTime.fromJSDate(date.start));
        // Standard approach for properly timezone-contexted events
        nextStartDate = DateTime.fromJSDate(nextOccurence).toUTC();
        // FORCE TIME CORRECTION: Ensure the next occurrence has the EXACT same local time as the original start date
        // This overrides any shifting/drift caused by RRule library quirks or timezone weirdness
        if (date.start && zone) {
            const originalStartInZone = DateTime.fromJSDate(date.start).setZone(zone);
            const calculatedInZone = nextStartDate.setZone(zone);
            if (originalStartInZone.hour !== calculatedInZone.hour ||
                originalStartInZone.minute !== calculatedInZone.minute) {
                debug("Time mismatch detected. Forcing original start time:", {
                    original: `${originalStartInZone.hour}:${originalStartInZone.minute}`,
                    calculated: `${calculatedInZone.hour}:${calculatedInZone.minute}`
                });
                // Construct new date preserving the calculated DATE (Year/Month/Day) but using original TIME
                const fixedInZone = calculatedInZone.set({
                    hour: originalStartInZone.hour,
                    minute: originalStartInZone.minute,
                    second: originalStartInZone.second,
                    millisecond: originalStartInZone.millisecond
                });
                nextStartDate = fixedInZone.toUTC();
            }
        }
        nextEndDate = nextStartDate.plus(duration);
        debug("Calculated next occurrence:", {
            nextStartDate: nextStartDate.toISO(),
            nextEndDate: nextEndDate.toISO(),
            duration: duration.toObject(),
        });
        const result = {
            start: nextStartDate.toJSDate(),
            end: nextEndDate.toJSDate(),
        };
        // Include correction flags if dtstart mismatch was detected
        if (needsRRuleCorrection) {
            result.needsRRuleCorrection = true;
            result.correctedDtstart = correctedDtstart;
        }
        return result;
    }
    catch (error) {
        console.log("Error calculating next occurrence: ", error, referenceDate, event);
        return event === null || event === void 0 ? void 0 : event.date;
    }
};
//# sourceMappingURL=parser.js.map